import { configurationApi } from '@/api';
import { DraggableTable, HelpFactoryComponent, Modal } from '@/components/common';
import { handleResponse, ScrollType } from '@/Helper';
import { SecuredComponents } from '@/model/AccountModel';
import { BaseColumns, GridColumn, ResponseObjectEntity, ResponseStatusCode } from '@/model/CommonModel';
import { ColumnConfiguration as ColumnConfigurationModel } from '@/model/ConfigurationModel';
import { HelpFeature } from '@/model/HelpModel';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TableRowSelection } from 'antd/es/table/interface';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import styles from './columnConfiguration.module.scss';

const topicId = 'ConfigureColumnHeaders';
const scroll: ScrollType = { x: '350px', y: '550px' };
type DataType = { index: number; Header: string } & GridColumn & BaseColumns;

const isTheConnectionSecure = isConnectionSecure();
const twoFactorColumnKey: string = 'TwoFactor';
type Props = {
	onHideModal: () => void;
	loadColumnConfiguration: () => Promise<ResponseObjectEntity<ColumnConfigurationModel>>;
	dispatchAction: () => void;
	securedComponent: SecuredComponents;
};

export const getColumnConfigurationToSave = (columnConfiguration: ColumnConfigurationModel): ColumnConfigurationModel => {
	return {
		...columnConfiguration,
		Columns: getGridColumns(columnConfiguration.Columns),
	};
};

export const getGridColumns = (columns: GridColumn[]): GridColumn[] =>
	columns.map<GridColumn>(
		(x: GridColumn): GridColumn => ({
			DataIndex: x.DataIndex,
			Header: x.Header,
			IsAuxColumn: x.IsAuxColumn,
			IsDateFormat: x.IsDateFormat,
			Key: x.Key,
			Order: x.Order,
			Visible: x.Visible,
			Width: x.Width,
		})
	);

const component: React.FC<Props> = ({ onHideModal, loadColumnConfiguration, dispatchAction, securedComponent }) => {
	//#region State Management Variables
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [dataSourceTable, setDataSourceTable] = useState<DataType[]>([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
	const [columnConfiguration, setColumnConfiguration] = useState<ColumnConfigurationModel>(undefined);
	const [error, setError] = useState<boolean>(false);
	const [hiddenColumns, setHiddenColumns] = useState<GridColumn[]>([]);
	//#endregion

	useEffect(() => {
		loadColumnConfiguration().then((response: ResponseObjectEntity<ColumnConfigurationModel>) => {
			if (!handleResponse(response)) {
				const columnConfigurationData = response.Entity;
				if (columnConfigurationData === undefined) {
					onHideModal();
				} else {
					if (securedComponent === SecuredComponents.Operator && !isTheConnectionSecure) {
						const securedConnectionColumn: GridColumn = columnConfigurationData.Columns.find(x => x.Key === twoFactorColumnKey);
						if (securedConnectionColumn) {
							securedConnectionColumn.Visible = false;
							securedConnectionColumn.IsAuxColumn = true;
						}
					}
					updateDataSourceTableAndSelectedRowKeys(columnConfigurationData);
					setHiddenColumns(getHiddenColumns(columnConfigurationData.Columns));
					setIsLoading(false);
				}
			} else {
				onHideModal();
			}
		});
	}, []);

	useEffect(() => {
		if (error && selectedRowKeys.length > 0) {
			setError(false);
		}
	}, [selectedRowKeys]);

	const getHiddenColumns = (columns: GridColumn[]) => {
		const newHiddenColumns: GridColumn[] = [];
		if (securedComponent === SecuredComponents.Operator && !isTheConnectionSecure) {
			const securedConnectionColumn: GridColumn = columns.find(x => x.Key === twoFactorColumnKey);
			if (securedConnectionColumn) {
				newHiddenColumns.push(securedConnectionColumn);
			}
		}

		return newHiddenColumns;
	};

	const updateDataSourceTableAndSelectedRowKeys = (columnConfiguration: ColumnConfigurationModel) => {
		const dataTable = columnConfiguration.Columns.filter(x => !x.IsAuxColumn).map((column: GridColumn, index) => ({
			...column,
			index,
			Header: _(column.Header),
			key: index.toString(),
		}));

		setSelectedRowKeys(dataTable.filter(f => f.Visible === true).map(m => m.index.toString()));
		setDataSourceTable(dataTable);
		setColumnConfiguration(columnConfiguration);
	};

	const onSelectChangeCheckBox = (selectedRowKeys: string[]) => {
		const updateItems: GridColumn[] = dataSourceTable.map<GridColumn>(m => {
			const found = selectedRowKeys.find(f => f === m.index.toString());
			return {
				...m,
				Order: dataSourceTable.indexOf(m) + 1,
				Visible: found !== undefined ? true : false,
			};
		});
		updateDataSourceTableAndSelectedRowKeys({ ...columnConfiguration, Columns: updateItems });
	};

	const addExtraColumns = (columns: GridColumn[]): GridColumn[] => {
		let newColumns: GridColumn[] = [...columns];

		hiddenColumns.forEach((hiddenColumn: GridColumn) => {
			const insertIndex: number = columns.findIndex(x => x.Order === hiddenColumn.Order);
			if (insertIndex === -1) {
				newColumns.push({ ...hiddenColumn, Order: columns.length + 1 });
			} else {
				newColumns.splice(insertIndex, 0, hiddenColumn);
			}

			newColumns = newColumns.map<GridColumn>((column: GridColumn, index: number) => ({ ...column, Order: index + 1 }));
		});

		return newColumns;
	};

	const handleOnSaveColumnConfiguration = e => {
		if (selectedRowKeys.length === 0) {
			setError(true);
			return;
		}

		let clonedState: ColumnConfigurationModel = { ...columnConfiguration };
		if (securedComponent === SecuredComponents.Alarm_Viewer || securedComponent === SecuredComponents.Operator) {
			clonedState.Columns = addExtraColumns(clonedState.Columns);
		}

		clonedState = getColumnConfigurationToSave(clonedState);

		configurationApi.saveColumnsConfiguration(clonedState, securedComponent).then(res => {
			if (res.ResponseStatusCode === ResponseStatusCode.Success) {
				dispatchAction();
				onHideModal();
			} else if (res.ResponseStatusCode === ResponseStatusCode.FailedValidation) {
				setError(true);
			} else {
				handleResponse(res);
			}
		});
	};

	const rowSelection: TableRowSelection<DataType> = {
		selectedRowKeys,
		onChange: onSelectChangeCheckBox,
		selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
	};

	const columns: ColumnsType<DataType> = [
		{
			title: _('Column'),
			dataIndex: 'Header',
		},
		{
			key: 'sort',
			title: _('Order'),
			dataIndex: 'sort',
			width: 55,
		},
	];

	const onSortEnd = ({ oldIndex, newIndex }) => {
		if (oldIndex !== newIndex) {
			const newData = arrayMove([].concat(dataSourceTable), oldIndex, newIndex).filter(el => !!el);
			const updateItems: GridColumn[] = newData.map<GridColumn>(m => {
				return {
					...m,
					Order: newData.indexOf(m) + 1,
				};
			});
			updateDataSourceTableAndSelectedRowKeys({ ...columnConfiguration, Columns: updateItems });
		}
	};

	return (
		<Modal
			keyboard={false}
			maskClosable={false}
			onCancel={onHideModal}
			onClickOk={() => null}
			width={450}
			title={_('ConfigureColumnHeaders')}
			visible={true}
			addHelpButton={<HelpFactoryComponent helpFeature={HelpFeature.Events} isLight={false} topicId={topicId} className={styles.helpButton} />}
			footer={[
				<Button id='saveColumnConfigurationButton' key='ok' type='primary' onClick={handleOnSaveColumnConfiguration} disabled={error}>
					{_('SaveChanges')}
				</Button>,
				<Button id='cancelColumnConfigurationButton' key='cancel' onClick={onHideModal}>
					{_('Cancel')}
				</Button>,
			]}>
			<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
				<div>
					<div>
						<p>{_('ConfigureColumnInstructions')}</p>
					</div>
					<div className={cx({ [styles.error]: error })}>
						<DraggableTable<DataType>
							id='columnConfigurationTable'
							scroll={scroll}
							pagination={false}
							dataSource={dataSourceTable}
							columns={columns}
							rowSelection={rowSelection}
							onSortEnd={onSortEnd}
						/>
					</div>
					{error && (
						<span className={styles.error} id='columnConfigurationErrorMessage'>
							{_('AtLeastOneItem')}
						</span>
					)}
				</div>
			</Spin>
		</Modal>
	);
};

const ColumnConfiguration = React.memo(component);

export { ColumnConfiguration };
